import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-sm text-gray-600"
}

import _ from 'lodash'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { DEFAULT_CENTER, DEFAULT_ZOOM, initialViewport, isLatLng } from '@/helpers/mapViewport'

const MIN_CATERING_POINTS = 3


export default {
  __name: 'GoogleMapComponent',
  props: {
  modelValue: { type: Array, default: () => [] },
  center: { type: Array, default: null },
  focus: { type: Array, default: null },
  mode: { type: String, default: 'region' },
  radiusMeters: { type: Number, default: null }
},
  emits: ['update:modelValue', 'update:closed'],
  setup(__props, { expose: __expose, emit: __emit }) {

const mapsPromise = google.maps.importLibrary('maps')

const props = __props
const emit = __emit
const el = ref(null)

const hasRegion = computed(() => props.modelValue?.length > 0)
const drawing = ref(false)

const toLatLng = ([lat, lng]) => ({ lat, lng })
const initialCenter = () => toLatLng(isLatLng(props.center) ? props.center : DEFAULT_CENTER)
const SHAPE_STYLE = { strokeColor: '#2563eb', strokeWeight: 2, fillColor: '#3b82f6', fillOpacity: 0.2 }
const CATERING_STYLE = { strokeColor: '#1B2420', strokeWeight: 2, fillColor: '#FDFF74', fillOpacity: 0.34 }

let map = null
let polygon = null
let clickListener = null
let drawPolyline = null
let startMarker = null

const polygonModel = () =>
  polygon
    ? polygon
        .getPath()
        .getArray()
        .map(p => [p.lat(), p.lng()])
    : []

const emitModel = () => emit('update:modelValue', polygonModel())

const attachEditListeners = poly => {
  const path = poly.getPath()
  google.maps.event.addListener(path, 'set_at', emitModel)
  google.maps.event.addListener(path, 'insert_at', emitModel)
  google.maps.event.addListener(path, 'remove_at', emitModel)
}

const clearPolygon = () => {
  if (polygon) {
    polygon.setMap(null)
    polygon = null
  }
}

const fitToPoints = points => {
  const bounds = new google.maps.LatLngBounds()
  for (const [lat, lng] of points) bounds.extend({ lat, lng })
  map.fitBounds(bounds)
}

const zoomMap = () => {
  const model = props.modelValue || []
  if (model.length) {
    fitToPoints(model)
  } else {
    map.setCenter(initialCenter())
    map.setZoom(DEFAULT_ZOOM)
  }
}

const renderModel = () => {
  if (props.mode === 'catering' || props.mode === 'radius') return
  if (!map) return
  const model = props.modelValue || []
  if (!model.length) {
    clearPolygon()
    zoomMap()
    return
  }
  if (polygon && _.isEqual(model, polygonModel())) return
  clearPolygon()
  polygon = new google.maps.Polygon({
    paths: model.map(([lat, lng]) => ({ lat, lng })),
    editable: true,
    map,
    ...SHAPE_STYLE
  })
  attachEditListeners(polygon)
  zoomMap()
}

const cancelDrawing = () => {
  if (clickListener) {
    google.maps.event.removeListener(clickListener)
    clickListener = null
  }
  if (drawPolyline) {
    drawPolyline.setMap(null)
    drawPolyline = null
  }
  if (startMarker) {
    startMarker.setMap(null)
    startMarker = null
  }
  if (drawing.value) {
    map.setOptions({ disableDoubleClickZoom: false })
    drawing.value = false
  }
}

const finishDrawing = () => {
  const path = drawPolyline.getPath().getArray()
  if (path.length < 3) return
  cancelDrawing()
  clearPolygon()
  polygon = new google.maps.Polygon({ paths: path, editable: true, map, ...SHAPE_STYLE })
  attachEditListeners(polygon)
  emitModel()
}

const newPolygon = () => {
  if (!map) return
  cancelDrawing()
  clearPolygon()
  drawing.value = true
  map.setOptions({ disableDoubleClickZoom: true })
  drawPolyline = new google.maps.Polyline({
    path: [],
    map,
    strokeColor: SHAPE_STYLE.strokeColor,
    strokeWeight: SHAPE_STYLE.strokeWeight
  })
  clickListener = map.addListener('click', e => {
    drawPolyline.getPath().push(e.latLng)
    if (!startMarker) {
      startMarker = new google.maps.Marker({
        position: e.latLng,
        map,
        clickable: true,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#ffffff',
          fillOpacity: 1,
          strokeColor: SHAPE_STYLE.strokeColor,
          strokeWeight: 3
        }
      })
      startMarker.addListener('click', finishDrawing)
    }
  })
}

const cateringPoints = ref([...(props.modelValue || [])])
const cateringClosed = ref(cateringPoints.value.length >= MIN_CATERING_POINTS)
const cateringUndo = ref([])
const cateringRedo = ref([])
let cateringPolygon = null
let cateringPolyline = null
let vertexMarkers = []
let storeMarker = null
let cateringClickListener = null

const clearVertexMarkers = () => {
  vertexMarkers.forEach(m => m.setMap(null))
  vertexMarkers = []
}

const cateringSnapshot = () => ({ points: [...cateringPoints.value], closed: cateringClosed.value })

const pushCateringUndo = () => {
  cateringUndo.value.push(cateringSnapshot())
  cateringRedo.value = []
}

const closeCatering = () => {
  if (cateringClosed.value || cateringPoints.value.length < MIN_CATERING_POINTS) return
  pushCateringUndo()
  cateringClosed.value = true
  renderCatering()
  emit('update:closed', true)
}

const startMarkerIcon = isCloser => ({
  path: google.maps.SymbolPath.CIRCLE,
  scale: isCloser ? 8 : 6,
  fillColor: isCloser ? '#1B2420' : '#FDFF74',
  fillOpacity: 1,
  strokeColor: isCloser ? '#FDFF74' : '#1B2420',
  strokeWeight: isCloser ? 3 : 2
})

const renderCatering = () => {
  if (!map) return
  clearVertexMarkers()
  if (cateringPolygon) {
    cateringPolygon.setMap(null)
    cateringPolygon = null
  }
  if (cateringPolyline) {
    cateringPolyline.setMap(null)
    cateringPolyline = null
  }
  const path = cateringPoints.value.map(([lat, lng]) => ({ lat, lng }))
  if (cateringClosed.value && path.length >= MIN_CATERING_POINTS) {
    cateringPolygon = new google.maps.Polygon({ paths: path, map, clickable: false, ...CATERING_STYLE })
  } else if (path.length >= 2) {
    cateringPolyline = new google.maps.Polyline({
      path,
      map,
      clickable: false,
      strokeColor: CATERING_STYLE.strokeColor,
      strokeWeight: CATERING_STYLE.strokeWeight
    })
  }
  if (cateringClosed.value) return
  const canClose = path.length >= MIN_CATERING_POINTS
  path.forEach((pos, index) => {
    const isStart = index === 0
    const marker = new google.maps.Marker({
      position: pos,
      map,
      clickable: isStart,
      title: isStart && canClose ? 'Click to close the delivery area' : undefined,
      icon: startMarkerIcon(isStart && canClose)
    })
    if (isStart) marker.addListener('click', closeCatering)
    vertexMarkers.push(marker)
  })
}

const renderStoreMarker = () => {
  if (storeMarker) {
    storeMarker.setMap(null)
    storeMarker = null
  }
  if (!map || !props.center) return
  const [lat, lng] = props.center
  storeMarker = new google.maps.Marker({
    position: { lat, lng },
    map,
    clickable: false,
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: '#1B2420',
      fillOpacity: 1,
      strokeColor: '#FDFF74',
      strokeWeight: 3
    }
  })
}

let radiusCircle = null

const clearRadiusCircle = () => {
  if (radiusCircle) {
    radiusCircle.setMap(null)
    radiusCircle = null
  }
}

const renderRadius = () => {
  if (!map) return
  clearRadiusCircle()
  renderStoreMarker()
  if (!props.center || !props.radiusMeters) return
  const [lat, lng] = props.center
  radiusCircle = new google.maps.Circle({
    center: { lat, lng },
    radius: props.radiusMeters,
    map,
    clickable: false,
    ...SHAPE_STYLE
  })
  map.fitBounds(radiusCircle.getBounds())
}

const restoreCatering = snapshot => {
  const wasClosed = cateringClosed.value
  cateringPoints.value = snapshot.points
  cateringClosed.value = snapshot.closed
  renderCatering()
  emit('update:modelValue', [...cateringPoints.value])
  if (cateringClosed.value !== wasClosed) emit('update:closed', cateringClosed.value)
}

const addCateringPoint = latLng => {
  if (cateringClosed.value) return
  pushCateringUndo()
  cateringPoints.value.push([latLng.lat(), latLng.lng()])
  renderCatering()
  emit('update:modelValue', [...cateringPoints.value])
}

const undo = () => {
  if (!cateringUndo.value.length) return
  cateringRedo.value.push(cateringSnapshot())
  restoreCatering(cateringUndo.value.pop())
}
const redo = () => {
  if (!cateringRedo.value.length) return
  cateringUndo.value.push(cateringSnapshot())
  restoreCatering(cateringRedo.value.pop())
}
const clear = () => {
  const wasClosed = cateringClosed.value
  pushCateringUndo()
  cateringPoints.value = []
  cateringClosed.value = false
  renderCatering()
  emit('update:modelValue', [])
  if (wasClosed) emit('update:closed', false)
}

watch(() => props.modelValue, renderModel)
watch([() => props.center, () => props.radiusMeters], () => {
  if (props.mode === 'radius') renderRadius()
})

onMounted(async () => {
  const { Map } = await mapsPromise
  const catering = props.mode === 'catering'
  const radius = props.mode === 'radius'
  const view = initialViewport({ points: catering ? props.modelValue : [], center: props.center, focus: props.focus })
  map = new Map(el.value, {
    center: toLatLng(view.center),
    zoom: view.zoom,
    ...(catering ? { fullscreenControl: false, mapTypeControl: false } : {})
  })
  renderModel()
  if (catering) {
    map.setOptions({ draggableCursor: 'crosshair' })
    if (view.bounds) fitToPoints(view.bounds)
    cateringClickListener = map.addListener('click', e => addCateringPoint(e.latLng))
    renderStoreMarker()
    renderCatering()
  }
  if (radius) renderRadius()
})

onBeforeUnmount(() => {
  cancelDrawing()
  clearPolygon()
  if (cateringClickListener) google.maps.event.removeListener(cateringClickListener)
  clearVertexMarkers()
  if (cateringPolygon) cateringPolygon.setMap(null)
  if (cateringPolyline) cateringPolyline.setMap(null)
  if (storeMarker) storeMarker.setMap(null)
  clearRadiusCircle()
})

__expose({
  undo,
  redo,
  clear,
  canUndo: computed(() => cateringUndo.value.length > 0),
  canRedo: computed(() => cateringRedo.value.length > 0),
  hasPoints: computed(() => cateringPoints.value.length > 0),
  closed: computed(() => cateringClosed.value)
})

return (_ctx, _cache) => {
  const _component_NeueButton = _resolveComponent("NeueButton")

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(__props.mode === 'catering' ? 'h-full w-full' : 'flex flex-col gap-3 items-start')
  }, [
    (__props.mode !== 'catering' && __props.mode !== 'radius')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: newPolygon
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(hasRegion.value ? 'Replace' : 'Add') + " region", 1)
            ]),
            _: 1
          }),
          (drawing.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, " Click to add points, then click the first marker to close the region. "))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "el",
      ref: el,
      class: _normalizeClass(
        __props.mode === 'catering'
          ? 'rounded-xl overflow-hidden w-full h-full'
          : 'rounded-xl overflow-hidden w-full h-[70vh] max-h-[50rem]'
      )
    }, null, 2)
  ], 2))
}
}

}